import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    setDoc,
    getDoc,
    collection,
    addDoc,
    query,
    where,
    onSnapshot,
    updateDoc,
    arrayUnion,
    Timestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";

// --- CONFIGURACIÓN DE FIREBASE ---
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// --- INICIALIZACIÓN DE SERVICIOS DE FIREBASE ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

// --- COMPONENTE PRINCIPAL: App ---
export default function App() {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [selectedCaseId, setSelectedCaseId] = useState(null);
    const [viewAs, setViewAs] = useState('student'); // 'student' or 'teacher'

    // Efecto para manejar el estado de autenticación
    useEffect(() => {
        let isInitialLoad = true;
        let unsubscribeUserDoc = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            if (unsubscribeUserDoc) {
                unsubscribeUserDoc();
                unsubscribeUserDoc = null;
            }

            if (currentUser) {
                setUser(currentUser);
                const userDocRef = doc(db, "users", currentUser.uid);
                
                unsubscribeUserDoc = onSnapshot(userDocRef, async (userDocSnap) => {
                    if (userDocSnap.exists()) {
                        const data = userDocSnap.data();
                        setUserData(data);

                        if (isInitialLoad) {
                            isInitialLoad = false;
                            if (data.role === 'teacher' || data.role === 'admin') {
                                setCurrentPage('roleSelection');
                            } else {
                                setViewAs('student');
                                setCurrentPage('dashboard');
                            }
                        }
                    } else {
                        const newUser = {
                            email: currentUser.email,
                            role: 'student',
                            status: 'approved',
                            createdAt: Timestamp.now()
                        };
                        await setDoc(userDocRef, newUser);
                        setUserData(newUser);
                        if (isInitialLoad) {
                            isInitialLoad = false;
                            setCurrentPage('dashboard');
                        }
                    }
                    setLoading(false);
                });
            } else {
                setUser(null);
                setUserData(null);
                setLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeUserDoc) unsubscribeUserDoc();
        };
    }, []);

    const handleLogout = () => {
        signOut(auth);
        setCurrentPage('dashboard');
        setViewAs('student');
    };

    const navigateTo = (page, caseId = null) => {
        setCurrentPage(page);
        setSelectedCaseId(caseId);
    }

    if (loading) {
        return <div className="flex items-center justify-center h-screen bg-gray-100"><Spinner /> <p className="ml-4 text-lg">Cargando Endo-Assistant...</p></div>;
    }

    return (
        <div className="bg-gray-100 min-h-screen font-sans text-base">
            <Navbar user={user} userData={userData} onLogout={handleLogout} navigateTo={navigateTo} viewAs={viewAs} setViewAs={setViewAs} setCurrentPage={setCurrentPage} />
            <main className="p-4 sm:p-6 md:p-8">
                {!user ? (
                    <AuthScreen />
                ) : (
                    <ContentRouter
                        userData={userData}
                        currentPage={currentPage}
                        navigateTo={navigateTo}
                        userId={user.uid}
                        selectedCaseId={selectedCaseId}
                        viewAs={viewAs}
                        setViewAs={setViewAs}
                        setCurrentPage={setCurrentPage}
                    />
                )}
            </main>
        </div>
    );
}

// --- ROUTER DE CONTENIDO ---
const ContentRouter = ({ userData, currentPage, navigateTo, userId, selectedCaseId, viewAs, setViewAs, setCurrentPage }) => {
    const role = userData?.role;
    const status = userData?.status;

    // TEMPORAL: Bypass de la pantalla de aprobación para la semana de pruebas
    // if (status !== 'approved' && currentPage !== 'pendingApproval') {
    //     return <PendingApprovalScreen />;
    // }

    switch (currentPage) {
        case 'newCase':
            return <ClinicalCaseForm userId={userId} navigateTo={navigateTo} />;
        case 'editCase':
            return <ClinicalCaseForm userId={userId} navigateTo={navigateTo} editCaseId={selectedCaseId} />;
        case 'caseDetail':
            return <CaseDetailView caseId={selectedCaseId} userId={userId} role={role} navigateTo={navigateTo} />;
        case 'admin':
            return role === 'admin' ? <AdminModule /> : <AccessDenied />;
        case 'teacher':
            return (role === 'teacher' || role === 'admin') ? <TeacherModule navigateTo={navigateTo} /> : <AccessDenied />;
        case 'roleSelection':
            return <RoleSelectionScreen setViewAs={setViewAs} setCurrentPage={setCurrentPage} />;
        case 'pendingApproval':
            return <PendingApprovalScreen />;
        case 'dashboard':
        default:
            if (viewAs === 'teacher' && (role === 'teacher' || role === 'admin')) {
                return <TeacherModule navigateTo={navigateTo} />;
            }
            return <StudentDashboard userId={userId} navigateTo={navigateTo} />;
    }
};


// --- COMPONENTES DE UI ---
const Navbar = ({ user, userData, onLogout, navigateTo, viewAs, setViewAs, setCurrentPage }) => {

    const handleViewChange = () => {
        if (viewAs === 'student') {
            setViewAs('teacher');
            setCurrentPage('teacher');
        } else {
            setViewAs('student');
            setCurrentPage('dashboard');
        }
    };

    return (
        <header className="bg-white shadow-md">
            <div className="container mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center cursor-pointer" onClick={() => navigateTo(userData?.role === 'student' ? 'dashboard' : 'roleSelection')}>
                        <img src="/photo_banner.jpg" alt="Endo-Assistant Banner" className="h-16 sm:h-20 object-contain" />
                    </div>
                    {user && userData?.status === 'approved' && <Button onClick={() => navigateTo(userData?.role === 'student' ? 'dashboard' : 'roleSelection')} variant="secondary">Inicio</Button>}
                </div>
                <div className="flex items-center space-x-4">
                    {user && userData?.status === 'approved' && (
                        <>
                            {(userData?.role === 'teacher' || userData?.role === 'admin') && (
                                <Button onClick={handleViewChange} variant="secondary">
                                    Ver como {viewAs === 'student' ? 'Docente' : 'Estudiante'}
                                </Button>
                            )}
                            {userData?.role === 'admin' &&
                                <Button onClick={() => navigateTo('admin')} variant="secondary">Admin</Button>
                            }
                            <div className="text-right">
                                <span className="text-gray-700">{userData?.email}</span>
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {userData?.role}
                                </span>
                            </div>
                        </>
                    )}
                    {user && <Button onClick={onLogout}>Cerrar Sesión</Button>}
                </div>
            </div>
        </header>
    )
};

const Button = ({ children, onClick, type = 'button', variant = 'primary', className = '', disabled = false }) => {
    const baseClasses = 'px-4 py-2 rounded-full font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm';
    const variants = {
        primary: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-md focus:ring-blue-500 border border-transparent',
        secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus:ring-gray-200 shadow-sm',
        danger: 'bg-gradient-to-r from-red-500 to-rose-600 text-white hover:from-red-600 hover:to-rose-700 hover:shadow-md focus:ring-red-500 border border-transparent'
    };
    return <button type={type} onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${className}`}>{children}</button>;
};

const Input = ({ id, label, type = 'text', value, onChange, required = false, placeholder = '' }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
        <input type={type} id={id} name={id} value={value} onChange={onChange} required={required} placeholder={placeholder} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
    </div>
);

const Select = ({ id, label, value, onChange, options, required = false }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
        <select id={id} name={id} value={value} onChange={onChange} required={required} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
    </div>
);

const RadioGroup = ({ id, label, value, onChange, options }) => (
    <fieldset>
        <legend className="block text-sm font-medium text-gray-700">{label}</legend>
        <div className="mt-2 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:space-x-4 flex-wrap">
            {options.map(opt => (
                <div key={opt.value} className="flex items-center">
                    <input id={`${id}-${opt.value}`} name={id} type="radio" value={opt.value} checked={value === opt.value} onChange={onChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" />
                    <label htmlFor={`${id}-${opt.value}`} className="ml-2 block text-sm text-gray-900">{opt.label}</label>
                </div>
            ))}
        </div>
    </fieldset>
);

const CheckboxGroup = ({ id, label, values, onChange, options }) => (
    <fieldset>
        <legend className="block text-sm font-medium text-gray-700">{label}</legend>
        <div className="mt-2 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:space-x-4 flex-wrap">
            {options.map(opt => (
                <div key={opt.value} className="flex items-center">
                    <input id={`${id}-${opt.value}`} name={opt.value} type="checkbox" checked={values[opt.value] || false} onChange={onChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded" />
                    <label htmlFor={`${id}-${opt.value}`} className="ml-2 block text-sm text-gray-900">{opt.label}</label>
                </div>
            ))}
        </div>
    </fieldset>
);


const TextArea = ({ id, label, value, onChange, rows = 4 }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
        <textarea id={id} name={id} rows={rows} value={value} onChange={onChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"></textarea>
    </div>
);

const Card = ({ children, className = '' }) => (
    <div className={`bg-white shadow-lg rounded-xl p-6 sm:p-8 ${className}`}>
        {children}
    </div>
);

const Spinner = () => (
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
);

const AccessDenied = () => (
    <Card className="text-center">
        <h2 className="text-2xl font-bold text-red-600">Acceso Denegado</h2>
        <p className="mt-4 text-gray-600">No tienes los permisos necesarios para ver esta sección.</p>
    </Card>
);

// --- COMPONENTES DE AUTENTICACIÓN ---
const AuthScreen = () => {
    const [authMode, setAuthMode] = useState('login'); // login, register, reset
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRegister = async () => {
        const allowedDomains = ['@uandresbello.edu', '@unab.cl'];
        const emailDomain = email.substring(email.lastIndexOf('@'));

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // TEMPORAL: Todos los usuarios son aprobados automáticamente durante semana de pruebas
        const userStatus = 'approved';

        await setDoc(doc(db, "users", user.uid), {
            email: user.email,
            role: 'student',
            status: userStatus,
            createdAt: Timestamp.now()
        });

        setMessage('¡Registro exitoso! Ya puedes usar la plataforma.');
        setAuthMode('login');
    };

    const handleLogin = async () => {
        await signInWithEmailAndPassword(auth, email, password);
    };

    const handleReset = async () => {
        await sendPasswordResetEmail(auth, email);
        setMessage('Se ha enviado un enlace para restablecer la contraseña a tu correo electrónico.');
        setAuthMode('login');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');
        try {
            if (authMode === 'login') await handleLogin();
            else if (authMode === 'register') await handleRegister();
            else if (authMode === 'reset') await handleReset();
        } catch (err) {
            setError(err.message.includes("auth/invalid-credential") ? "Email o contraseña incorrectos." : "Error: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (authMode === 'reset') {
            return {
                title: 'Restablecer Contraseña',
                form: (
                    <>
                        <Input id="email" label="Correo Electrónico" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </>
                ),
                buttonText: 'Enviar Enlace',
                footerText: '¿Recuerdas tu contraseña?',
                footerLinkText: 'Iniciar Sesión',
                switchMode: 'login'
            };
        }
        if (authMode === 'register') {
            return {
                title: 'Crear Cuenta',
                form: (
                    <>
                        <Input id="email" label="Correo Electrónico" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                        <Input id="password" label="Contraseña" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    </>
                ),
                buttonText: 'Registrarse',
                footerText: '¿Ya tienes una cuenta?',
                footerLinkText: 'Inicia Sesión',
                switchMode: 'login'
            };
        }
        // Default to login
        return {
            title: 'Iniciar Sesión',
            form: (
                <>
                    <Input id="email" label="Correo Electrónico" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    <Input id="password" label="Contraseña" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <div className="text-right text-sm">
                        <button type="button" onClick={() => setAuthMode('reset')} className="font-medium text-blue-600 hover:text-blue-500">
                            ¿Olvidaste tu contraseña?
                        </button>
                    </div>
                </>
            ),
            buttonText: 'Ingresar',
            footerText: '¿No tienes una cuenta?',
            footerLinkText: 'Regístrate',
            switchMode: 'register'
        };
    };

    const { title, form, buttonText, footerText, footerLinkText, switchMode } = renderContent();

    return (
        <div className="max-w-md mx-auto">
            <Card>
                <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">{title}</h2>
                {message && <p className="text-green-600 bg-green-50 p-3 rounded-md text-sm mb-4">{message}</p>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {form}
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? <Spinner /> : buttonText}
                    </Button>
                </form>
                <p className="mt-4 text-center text-sm">
                    {footerText}
                    <button onClick={() => setAuthMode(switchMode)} className="ml-1 font-medium text-blue-600 hover:text-blue-500">
                        {footerLinkText}
                    </button>
                </p>
            </Card>
        </div>
    );
};

// --- MÓDULO ESTUDIANTE ---
const StudentDashboard = ({ userId, navigateTo }) => {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, "clinicalCases"), where("studentId", "==", userId));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const casesData = [];
            querySnapshot.forEach((doc) => {
                casesData.push({ id: doc.id, ...doc.data() });
            });
            // Ordenar por fecha de creación descendente
            casesData.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
            setCases(casesData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching cases: ", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    return (
        <div className="container mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Mis Casos Clínicos</h1>
                <Button onClick={() => navigateTo('newCase')}>+ Nuevo Caso Clínico</Button>
            </div>
            <Card>
                {loading ? (
                    <div className="text-center p-8"><Spinner /></div>
                ) : cases.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aún no has creado ningún caso. ¡Crea uno para empezar!</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código del Caso</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de Creación</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Hipótesis</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interacciones</th>
                                    <th scope="col" className="relative px-6 py-3"><span className="sr-only">Ver</span></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {cases.map(c => (
                                    <tr key={c.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                            {c.caseId}
                                            {c.hasUnreadTeacherComment && (
                                                <span className="ml-2 inline-flex relative h-3 w-3">
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" title="Nuevo Comentario Docente"></span>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(c.createdAt.toDate()).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.interactions[c.interactions.length - 1]?.hypothesis || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.interactions.length}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button onClick={() => navigateTo('caseDetail', c.id)} className="text-blue-600 hover:text-blue-900">Ver Detalles</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
};

// --- FORMULARIO DE CASO CLÍNICO ---
const ClinicalCaseForm = ({ userId, navigateTo, editCaseId = null }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        // Datos Generales
        fecha: new Date().toISOString().split('T')[0],
        edadPaciente: '',
        antecedentesMorbidos: '',
        diente: '',
        historiaDiente: '',
        // Sintomatología Actual
        dolor: 'ausente',
        dolorProvocado: 'no',
        dolorProvocadoPor: { frio: false, calor: false, posicionDecubito: false, dulce: false, acido: false, masticacion: false },
        dolorEspontaneo: 'no',
        intensidadDolor: 'leve',
        localizacionDolor: 'localizado',
        duracionDolor: 'fugaz',
        // Examen Intraoral
        tejidosBlandos: '',
        sensacionDienteElongado: 'no',
        cambioColor: 'no',
        caries: 'sinCaries',
        cavidad: 'no',
        obturacionTemporal: 'no',
        obturacionDefinitiva: 'no',
        protesisFija: 'no',
        protesisProvisoria: 'no',
        fracturaCoronaria: 'noAplica',
        fracturaCoronariaOtro: '',
        movilidad: 'grado0',
        presenciaSacoPeriodontal: 'no',
        profundidadSacoPeriodontal: '',
        aumentoVolumen: 'no',
        tractoSinusal: 'noAplica',
        // Tests
        dienteControl: '',
        testFrio: 'normal',
        testCalor: 'normal',
        testElectrico: 'noAplica',
        valorTestElectrico: '',
        testExploracion: 'noAplica',
        testPercusion: 'normal',
        testPalpacion: 'normal',
        observacionesTests: '',
        // Examen Radiográfico
        lineaPeriodontalApical: 'normal',
        areaRadiolucida: 'no',
        tamañoRadiolucida: '',
        limitesRadiolucida: '',
        areaRadiopaca: 'no',
        otrosAntecedentesImagenologicos: '',
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editCaseId) {
            const fetchCase = async () => {
                try {
                    const docRef = doc(db, "clinicalCases", editCaseId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setFormData(docSnap.data().clinicalData);
                    }
                } catch (err) {
                    console.error("Error fetching case for edit:", err);
                }
            };
            fetchCase();
        }
    }, [editCaseId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                dolorProvocadoPor: {
                    ...prev.dolorProvocadoPor,
                    [name]: checked
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editCaseId) {
                const docRef = doc(db, 'clinicalCases', editCaseId);
                await updateDoc(docRef, {
                    clinicalData: formData,
                });
                navigateTo('caseDetail', editCaseId);
            } else {
                const caseId = `ENDO-${Date.now().toString().slice(-6)}`;
                const docRef = await addDoc(collection(db, 'clinicalCases'), {
                    caseId,
                    studentId: userId,
                    studentEmail: auth.currentUser?.email || '',
                    clinicalData: formData,
                    interactions: [],
                    teacherComments: [],
                    createdAt: Timestamp.now(),
                });
                navigateTo('caseDetail', docRef.id);
            }
        } catch (error) {
            console.error("Error al guardar caso: ", error);
            alert("Hubo un error al guardar el caso.");
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return <GeneralAndSymptomsStep data={formData} handleChange={handleChange} />;
            case 2:
                return <IntraoralExamStep data={formData} handleChange={handleChange} />;
            case 3:
                return <TestsAndRadiographicStep data={formData} handleChange={handleChange} />;
            default:
                return null;
        }
    }

    return (
        <Card className="max-w-5xl mx-auto">
            <form onSubmit={handleSubmit}>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Nueva Ficha Clínica</h2>
                <p className="text-sm text-gray-500 mb-6">Complete la información del caso clínico.</p>

                <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button type="button" onClick={() => setStep(1)} className={`${step === 1 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>Paso 1. Anamnesis y Sintomatología</button>
                        <button type="button" onClick={() => setStep(2)} className={`${step === 2 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>Paso 2. Examen Intraoral</button>
                        <button type="button" onClick={() => setStep(3)} className={`${step === 3 ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>Paso 3. Tests de Sensibilidad y Ex. Radiográfico</button>
                    </nav>
                </div>

                <div className="space-y-6 min-h-[400px]">
                    {renderStep()}
                </div>

                <div className="mt-8 pt-5 border-t border-gray-200 flex justify-between items-center">
                    <div>
                        {step > 1 && <Button type="button" variant="secondary" onClick={() => setStep(s => s - 1)}>Anterior</Button>}
                    </div>
                    <div>
                        {step < 3 && <Button type="button" onClick={() => setStep(s => s + 1)}>Siguiente</Button>}
                        {step === 3 && (
                            <Button type="submit" disabled={loading}>
                                {loading ? <Spinner /> : "Crear Ficha y Continuar"}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </Card>
    );
};

// --- PASOS DEL FORMULARIO ---
const SectionTitle = ({ children }) => <h3 className="text-lg font-medium leading-6 text-gray-900 col-span-full border-b pb-2 mb-4">{children}</h3>;

const GeneralAndSymptomsStep = ({ data, handleChange }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <SectionTitle>Datos Generales</SectionTitle>
        <Input id="fecha" label="Fecha" type="date" value={data.fecha} onChange={handleChange} />
        <Input id="diente" label="Diente (nomenclatura internacional)" type="number" value={data.diente} onChange={handleChange} />
        <Input id="edadPaciente" label="Edad del paciente" type="number" value={data.edadPaciente} onChange={handleChange} />
        <div />
        <div className="md:col-span-2">
            <TextArea id="antecedentesMorbidos" label="Antecedentes mórbidos" value={data.antecedentesMorbidos} onChange={handleChange} rows={3} />
        </div>
        <div className="md:col-span-2">
            <TextArea id="historiaDiente" label="Historia del diente afectado" value={data.historiaDiente} onChange={handleChange} rows={3} />
        </div>

        <SectionTitle>Sintomatología Actual</SectionTitle>
        <div className="md:col-span-2">
            <RadioGroup id="dolor" label="Dolor" value={data.dolor} onChange={handleChange} options={[{ value: 'presente', label: 'Presente' }, { value: 'ausente', label: 'Ausente' }]} />
        </div>

        {data.dolor === 'presente' && (
            <>
                <RadioGroup id="dolorEspontaneo" label="Dolor espontáneo" value={data.dolorEspontaneo} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
                <RadioGroup id="dolorProvocado" label="Dolor provocado" value={data.dolorProvocado} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />

                {data.dolorProvocado === 'si' && (
                    <div className="md:col-span-2">
                        <CheckboxGroup id="dolorProvocadoPor" label="Dolor provocado por" values={data.dolorProvocadoPor} onChange={handleChange} options={[
                            { value: 'frio', label: 'Frío' }, { value: 'calor', label: 'Calor' }, { value: 'posicionDecubito', label: 'Posición decúbito' },
                            { value: 'dulce', label: 'Dulce' }, { value: 'acido', label: 'Ácido' }, { value: 'masticacion', label: 'Masticación' }
                        ]} />
                    </div>
                )}
                <Select id="intensidadDolor" label="Intensidad del dolor" value={data.intensidadDolor} onChange={handleChange} options={[{ value: 'leve', label: 'Leve' }, { value: 'moderado', label: 'Moderado' }, { value: 'severo', label: 'Severo' }]} />
                <Select id="localizacionDolor" label="Localización del dolor" value={data.localizacionDolor} onChange={handleChange} options={[{ value: 'localizado', label: 'Localizado' }, { value: 'irradiado', label: 'Irradiado' }, { value: 'referido', label: 'Referido' }]} />
                <Select id="duracionDolor" label="Duración del dolor" value={data.duracionDolor} onChange={handleChange} options={[{ value: 'fugaz', label: 'Fugaz' }, { value: 'persistente', label: 'Persistente' }]} />
                <div /> {/* Spacer */}
            </>
        )}
    </div>
);

const IntraoralExamStep = ({ data, handleChange }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <SectionTitle>Examen Intraoral</SectionTitle>
        <div className="md:col-span-2">
            <TextArea id="tejidosBlandos" label="Tejidos blandos" value={data.tejidosBlandos} onChange={handleChange} rows={3} />
        </div>
        <RadioGroup id="sensacionDienteElongado" label="Sensación de diente elongado" value={data.sensacionDienteElongado} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        <RadioGroup id="cambioColor" label="Cambio de coloración" value={data.cambioColor} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        <Select id="cavidad" label="Cavidad" value={data.cavidad} onChange={handleChange} options={[
            { value: 'no', label: 'No' },
            { value: 'superficial', label: 'Superficial' },
            { value: 'profunda', label: 'Profunda' },
            { value: 'penetrante', label: 'Penetrante' }
        ]} />
        <Select id="obturacionTemporal" label="Obturación temporal" value={data.obturacionTemporal} onChange={handleChange} options={[
            { value: 'no', label: 'No' },
            { value: 'superficial', label: 'Superficial' },
            { value: 'profunda', label: 'Profunda' },
            { value: 'penetrante', label: 'Penetrante' }
        ]} />
        <Select id="obturacionDefinitiva" label="Obturación definitiva" value={data.obturacionDefinitiva} onChange={handleChange} options={[
            { value: 'no', label: 'No' },
            { value: 'superficial', label: 'Superficial' },
            { value: 'profunda', label: 'Profunda' },
            { value: 'penetrante', label: 'Penetrante' }
        ]} />
        <RadioGroup id="protesisFija" label="Prótesis fija" value={data.protesisFija} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        <RadioGroup id="protesisProvisoria" label="Prótesis provisoria" value={data.protesisProvisoria} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        <RadioGroup id="aumentoVolumen" label="Aumento de volumen" value={data.aumentoVolumen} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        <Select id="caries" label="Caries" value={data.caries} onChange={handleChange} options={[{ value: 'sinCaries', label: 'Sin caries' }, { value: 'superficial', label: 'Superficial' }, { value: 'profunda', label: 'Profunda' }, { value: 'penetrante', label: 'Penetrante' }]} />
        <Select id="movilidad" label="Movilidad" value={data.movilidad} onChange={handleChange} options={[{ value: 'grado0', label: 'Fisiológica (Grado 0)' }, { value: 'grado1', label: 'Grado 1' }, { value: 'grado2', label: 'Grado 2' }, { value: 'grado3', label: 'Grado 3' }]} />
        <Select id="tractoSinusal" label="Tracto sinusal" value={data.tractoSinusal} onChange={handleChange} options={[{ value: 'noAplica', label: 'No Aplica' }, { value: 'activo', label: 'Activo' }, { value: 'inactivo', label: 'Inactivo' }, { value: 'cicatriz', label: 'Cicatriz' }]} />
        <div className="md:col-span-2">
            <RadioGroup id="presenciaSacoPeriodontal" label="Presencia de saco periodontal" value={data.presenciaSacoPeriodontal} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        </div>
        {data.presenciaSacoPeriodontal === 'si' && (
            <Input id="profundidadSacoPeriodontal" label="Profundidad del saco (mm)" type="number" value={data.profundidadSacoPeriodontal} onChange={handleChange} />
        )}
        <div className="md:col-span-2">
            <Select id="fracturaCoronaria" label="Fractura coronaria por trauma" value={data.fracturaCoronaria} onChange={handleChange} options={[{ value: 'noAplica', label: 'No aplica' }, { value: 'complicada', label: 'Complicada' }, { value: 'noComplicada', label: 'No complicada' }, { value: 'otro', label: 'Otro' }]} />
        </div>
        {data.fracturaCoronaria === 'otro' && (
            <div className="md:col-span-2">
                <Input id="fracturaCoronariaOtro" label="Especifique otra fractura" type="text" value={data.fracturaCoronariaOtro} onChange={handleChange} />
            </div>
        )}
    </div>
);

const TestsAndRadiographicStep = ({ data, handleChange }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <SectionTitle>Tests Pulpares y Periapicales</SectionTitle>
        <Input id="dienteControl" label="Diente control" type="number" value={data.dienteControl} onChange={handleChange} />
        <div /> {/* Empty div for grid alignment */}
        <Select id="testFrio" label="Test Frío" value={data.testFrio} onChange={handleChange} options={[{ value: 'normal', label: 'Normal' }, { value: 'aumentado', label: 'Aumentado' }, { value: 'disminuido', label: 'Disminuido' }, { value: 'noResponde', label: 'No responde' }]} />
        <Select id="testCalor" label="Test Calor" value={data.testCalor} onChange={handleChange} options={[{ value: 'normal', label: 'Normal' }, { value: 'aumentado', label: 'Aumentado' }, { value: 'disminuido', label: 'Disminuido' }, { value: 'noResponde', label: 'No responde' }]} />
        <Select id="testElectrico" label="Test Eléctrico" value={data.testElectrico} onChange={handleChange} options={[{ value: 'noAplica', label: 'No Aplica' }, { value: 'normal', label: 'Normal' }, { value: 'aumentado', label: 'Aumentado' }, { value: 'disminuido', label: 'Disminuido' }, { value: 'noResponde', label: 'No responde' }]} />
        <Select id="testExploracion" label="Test Exploración" value={data.testExploracion} onChange={handleChange} options={[
            { value: 'noAplica', label: 'No Aplica' },
            { value: 'normal', label: 'Normal' },
            { value: 'aumentado', label: 'Aumentado' },
            { value: 'noResponde', label: 'No responde' }
        ]} />
        {data.testElectrico !== 'noAplica' && (
            <div className="md:col-span-2">
                <Input id="valorTestElectrico" label="Valor registrado" type="text" value={data.valorTestElectrico} onChange={handleChange} />
            </div>
        )}
        <RadioGroup id="testPercusion" label="Test Percusión" value={data.testPercusion} onChange={handleChange} options={[{ value: 'normal', label: 'Normal' }, { value: 'aumentado', label: 'Aumentado' }]} />
        <RadioGroup id="testPalpacion" label="Test Palpación" value={data.testPalpacion} onChange={handleChange} options={[{ value: 'normal', label: 'Normal' }, { value: 'aumentado', label: 'Aumentado' }]} />

        <div className="md:col-span-2">
            <TextArea id="observacionesTests" label="Observaciones" value={data.observacionesTests} onChange={handleChange} rows={3} />
        </div>

        <SectionTitle>Examen Radiográfico</SectionTitle>
        <Select id="lineaPeriodontalApical" label="Línea periodontal apical" value={data.lineaPeriodontalApical} onChange={handleChange} options={[{ value: 'normal', label: 'Normal' }, { value: 'engrosada', label: 'Engrosada' }, { value: 'ausente', label: 'Ausente' }]} />
        <div /> {/* Empty div for grid alignment */}
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <RadioGroup id="areaRadiolucida" label="Área Radiolúcida" value={data.areaRadiolucida} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
            {data.areaRadiolucida === 'si' && (
                <>
                    <Input id="tamañoRadiolucida" label="Tamaño (mm)" type="number" value={data.tamañoRadiolucida} onChange={handleChange} />
                    <Select id="limitesRadiolucida" label="Límites" value={data.limitesRadiolucida} onChange={handleChange} options={[
                        { value: '', label: 'Seleccione un límite...' },
                        { value: 'netos', label: 'Netos' },
                        { value: 'difusos', label: 'Difusos' },
                        { value: 'corticalizados', label: 'Corticalizados' }
                    ]} />
                </>
            )}
            <RadioGroup id="areaRadiopaca" label="Área Radiopaca" value={data.areaRadiopaca} onChange={handleChange} options={[{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }]} />
        </div>
        <div className="md:col-span-2">
            <TextArea id="otrosAntecedentesImagenologicos" label="Otros antecedentes imagenológicos" value={data.otrosAntecedentesImagenologicos} onChange={handleChange} rows={3} />
        </div>
    </div>
);

// --- VISTA DETALLADA DEL CASO Y MÓDULO DE RAZONAMIENTO ---
const CaseDetailView = ({ caseId, userId, role, navigateTo }) => {
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('analysis'); // analysis, history

    useEffect(() => {
        if (!caseId) return;
        const docRef = doc(db, "clinicalCases", caseId);
        const unsubscribe = onSnapshot(docRef, (doc) => {
            if (doc.exists()) {
                setCaseData({ id: doc.id, ...doc.data() });
            } else {
                console.error("No such document!");
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [caseId]);

    const canInteract = caseData ? (userId === caseData.studentId) : false;

    useEffect(() => {
        if (activeTab === 'teacher' && canInteract && caseData?.hasUnreadTeacherComment) {
            const clearNotification = async () => {
                try {
                    await updateDoc(doc(db, "clinicalCases", caseId), { hasUnreadTeacherComment: false });
                } catch (e) { console.error("Error clearing notification", e); }
            };
            clearNotification();
        }
    }, [activeTab, canInteract, caseData?.hasUnreadTeacherComment, caseId]);

    if (loading) return <div className="flex justify-center mt-10"><Spinner /></div>;
    if (!caseData) return <Card><p>No se pudo cargar el caso.</p></Card>;

    const TabButton = ({ tabName, currentTab, setTab, children, hasNotification }) => (
        <button
            onClick={() => setTab(tabName)}
            className={`px-4 py-2 text-lg font-medium rounded-t-lg transition-colors duration-200 relative ${currentTab === tabName
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-blue-600'
                }`}
        >
            {children}
            {hasNotification && (
                <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
            )}
        </button>
    );

    return (
        <div className="container mx-auto max-w-full space-y-8">
            <Card>
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Detalle del Caso Clínico</h1>
                        <p className="mt-1 text-lg font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded-md inline-block">Código: {caseData.caseId}</p>
                        {(role === 'teacher' || role === 'admin') && <p className="mt-2 text-sm text-gray-600 font-medium">Creado por: {caseData.studentEmail || 'No disponible'}</p>}
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-500 mb-4">Creado el: {new Date(caseData.createdAt.toDate()).toLocaleString()}</p>
                        <div className="flex flex-wrap items-center gap-3 justify-end">
                            {canInteract && <Button variant="secondary" onClick={() => navigateTo('editCase', caseId)}>Editar Ficha</Button>}
                            {(role === 'teacher' || role === 'admin') && caseData.interactions?.length > 1 && (
                                <Button
                                    variant="secondary"
                                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                    onClick={async () => {
                                        if (window.confirm("¿Estás seguro de que deseas eliminar las iteraciones duplicadas? Se mantendrá solo una versión de cada respuesta idéntica.")) {
                                            try {
                                                const uniqueInteractions = [];
                                                const seen = new Set();

                                                caseData.interactions.forEach(inter => {
                                                    const key = `${inter.hypothesis}|${inter.justification}|${inter.treatment}`;
                                                    if (!seen.has(key)) {
                                                        seen.add(key);
                                                        uniqueInteractions.push(inter);
                                                    }
                                                });

                                                if (uniqueInteractions.length < caseData.interactions.length) {
                                                    await updateDoc(doc(db, "clinicalCases", caseId), {
                                                        interactions: uniqueInteractions
                                                    });
                                                    alert("Limpieza completada con éxito.");
                                                } else {
                                                    alert("No se encontraron iteraciones duplicadas idénticas.");
                                                }
                                            } catch (err) {
                                                console.error(err);
                                                alert("Error al limpiar duplicados.");
                                            }
                                        }
                                    }}
                                >
                                    🧹 Limpiar Duplicados
                                </Button>
                            )}
                            <Button variant="primary" onClick={() => navigateTo('newCase')}>+ Nueva Ficha</Button>
                            {(role === 'teacher' || role === 'admin') && <Button variant="secondary" onClick={() => navigateTo('teacher')}>Volver al Panel Docente</Button>}
                        </div>
                    </div>
                </div>
            </Card>

            <div>
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-6">
                        <TabButton tabName="analysis" currentTab={activeTab} setTab={setActiveTab}>Ficha y Análisis</TabButton>
                        <TabButton tabName="history" currentTab={activeTab} setTab={setActiveTab}>Retroalimentación e Historial</TabButton>
                        {(role === 'teacher' || role === 'admin' || (caseData.teacherComments && caseData.teacherComments.length > 0)) && (
                            <TabButton tabName="teacher" currentTab={activeTab} setTab={setActiveTab} hasNotification={canInteract && caseData.hasUnreadTeacherComment}>
                                Acompañamiento Docente
                            </TabButton>
                        )}
                    </nav>
                </div>

                <div className="mt-6">
                    {activeTab === 'analysis' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                            {/* Columna Izquierda: Ficha Clínica (Scrolleable independientemente) */}
                            <div className="overflow-y-auto max-h-[80vh] pr-2 custom-scrollbar">
                                <ClinicalDataDisplay clinicalData={caseData.clinicalData} />
                            </div>
                            {/* Columna Derecha: Módulo de Razonamiento (Fijo/Sticky) */}
                            <div className="sticky top-6">
                                {canInteract ? <ReasoningModule caseData={caseData} onFeedbackReceived={() => setActiveTab('history')} /> : <Card><p>Solo el estudiante que creó el caso puede enviar un nuevo análisis.</p></Card>}
                            </div>
                        </div>
                    )}

                    {activeTab === 'history' && <InteractionsHistory interactions={caseData.interactions} />}

                    {activeTab === 'teacher' && (role === 'teacher' || role === 'admin' || (caseData.teacherComments && caseData.teacherComments.length > 0)) && (
                        <TeacherFeedback caseId={caseId} comments={caseData.teacherComments} role={role} />
                    )}
                </div>
            </div>
        </div>
    );
};

const TeacherFeedback = ({ caseId, comments, role }) => {
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editText, setEditText] = useState('');

    const handleSaveComment = async () => {
        if (!newComment.trim()) return;
        setLoading(true);
        try {
            const caseDocRef = doc(db, "clinicalCases", caseId);
            await updateDoc(caseDocRef, {
                hasUnreadTeacherComment: true,
                teacherComments: arrayUnion({
                    comment: newComment,
                    timestamp: Timestamp.now(),
                    teacherEmail: auth.currentUser?.email || 'Docente'
                })
            });
            setNewComment('');
        } catch (err) {
            console.error("Error saving comment", err);
            alert("No se pudo guardar el comentario.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (index) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar este comentario?")) return;
        setLoading(true);
        try {
            const newComments = [...comments];
            newComments.splice(index, 1);
            await updateDoc(doc(db, "clinicalCases", caseId), {
                teacherComments: newComments
            });
        } catch (err) {
            console.error(err);
            alert("No se pudo eliminar el comentario.");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEdit = async (index) => {
        if (!editText.trim()) return;
        setLoading(true);
        try {
            const newComments = [...comments];
            newComments[index].comment = editText;
            newComments[index].edited = true;
            await updateDoc(doc(db, "clinicalCases", caseId), {
                teacherComments: newComments
            });
            setEditingIndex(null);
        } catch (err) {
            console.error(err);
            alert("No se pudo guardar la edición.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Acompañamiento Docente</h2>

            <div className="mb-6 space-y-4">
                {!comments || comments.length === 0 ? (
                    <p className="text-gray-500">Aún no hay comentarios.</p>
                ) : (
                    comments.map((c, i) => (
                        <div key={i} className="bg-blue-50 p-4 rounded-md border border-blue-100">
                            <div className="flex justify-between items-start text-sm text-gray-500 mb-2">
                                <div>
                                    <span className="font-semibold">{c.teacherEmail}</span>
                                    <span className="ml-2 text-xs">{c.timestamp ? new Date(c.timestamp.toDate()).toLocaleString() : ''} {c.edited && '(Editado)'}</span>
                                </div>
                                {(role === 'teacher' || role === 'admin') && (c.teacherEmail === auth.currentUser?.email || role === 'admin') && (
                                    <div className="flex space-x-2">
                                        <button onClick={() => { setEditingIndex(i); setEditText(c.comment); }} className="text-blue-600 hover:text-blue-800" title="Editar">✏️</button>
                                        <button onClick={() => handleDelete(i)} className="text-red-600 hover:text-red-800" title="Eliminar">🗑️</button>
                                    </div>
                                )}
                            </div>

                            {editingIndex === i ? (
                                <div className="mt-2">
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        rows="3"
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                    />
                                    <div className="flex justify-end space-x-2 mt-2">
                                        <button onClick={() => setEditingIndex(null)} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancelar</button>
                                        <button onClick={() => handleSaveEdit(i)} disabled={loading} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Guardar</button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-800 whitespace-pre-wrap">{c.comment}</p>
                            )}
                        </div>
                    ))
                )}
            </div>

            {(role === 'teacher' || role === 'admin') && (
                <div className="border-t border-gray-200 pt-4">
                    <TextArea
                        id="newComment"
                        label="Añadir nueva observación"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={4}
                    />
                    <div className="flex justify-end pt-3">
                        <Button onClick={handleSaveComment} disabled={loading || !newComment.trim()}>
                            {loading ? <Spinner /> : "Guardar Comentario"}
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
};

const ClinicalDataDisplay = ({ clinicalData }) => {
    const [openCategory, setOpenCategory] = useState('generales');

    const categories = [
        {
            id: 'generales', title: 'Datos Generales', icon: '📋',
            fields: ['fecha', 'diente', 'edadPaciente', 'antecedentesMorbidos', 'historiaDiente']
        },
        {
            id: 'sintomatologia', title: 'Sintomatología Actual', icon: '🤒',
            fields: ['dolor', 'dolorEspontaneo', 'dolorProvocado', 'dolorProvocadoPor', 'intensidadDolor', 'localizacionDolor', 'duracionDolor']
        },
        {
            id: 'examen_clinico', title: 'Examen Clínico', icon: '🦷',
            fields: ['tejidosBlandos', 'sensacionDienteElongado', 'cambioColor', 'caries', 'cavidad', 'obturacionTemporal', 'obturacionDefinitiva', 'protesisFija', 'protesisProvisoria', 'fracturaCoronaria', 'fracturaCoronariaOtro', 'movilidad', 'presenciaSacoPeriodontal', 'profundidadSacoPeriodontal', 'aumentoVolumen', 'tractoSinusal']
        },
        {
            id: 'tests_clinicos', title: 'Tests Clínicos', icon: '⚡',
            fields: ['dienteControl', 'testFrio', 'testCalor', 'testElectrico', 'valorTestElectrico', 'testExploracion', 'testPercusion', 'testPalpacion', 'observacionesTests']
        },
        {
            id: 'radiografico', title: 'Hallazgos Radiográficos', icon: '📷',
            fields: ['lineaPeriodontalApical', 'areaRadiolucida', 'tamañoRadiolucida', 'limitesRadiolucida', 'areaRadiopaca', 'otrosAntecedentesImagenologicos']
        }
    ];

    const formatKey = (key) => {
        const formatted = key.replace(/([A-Z])/g, ' $1').trim();
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const formatValue = (key, value) => {
        if (value === '' || value === null || value === undefined) return <span className="text-gray-400">No especificado</span>;
        if (typeof value === 'boolean') return value ? 'Sí' : 'No';
        if (key === 'dolorProvocadoPor' && typeof value === 'object') {
            const selected = Object.entries(value).filter(([, val]) => val).map(([k]) => formatKey(k)).join(', ');
            return selected || 'Ninguno';
        }
        if (typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9]*$/.test(value)) {
            if (value.toLowerCase() === 'si') return 'Sí';
            if (value.toLowerCase() === 'no') return 'No';
            if (value === 'noResponde') return 'No responde';
            const formatted = value.replace(/([A-Z0-9]+)/g, ' $1').trim();
            return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        }
        return value.toString();
    }

    const toggleCategory = (id) => {
        setOpenCategory(openCategory === id ? null : id);
    };

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Ficha Clínica Registrada</h2>
            {categories.map((category) => {
                const isOpen = openCategory === category.id;

                // Verificar si hay al menos un dato válido en la categoría
                const hasData = category.fields.some(key => {
                    const val = clinicalData[key];
                    return val !== undefined && val !== '' && val !== null && !(typeof val === 'object' && Object.keys(val).length === 0);
                });

                return (
                    <div key={category.id} className={`border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 ${isOpen ? 'shadow-md ring-1 ring-blue-500 border-transparent' : 'bg-white hover:border-gray-300'}`}>
                        <button
                            onClick={() => toggleCategory(category.id)}
                            className={`w-full flex items-center justify-between px-5 py-4 focus:outline-none transition-colors ${isOpen ? 'bg-blue-50/50' : 'bg-white hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">{category.icon}</span>
                                <h3 className={`text-lg font-bold ${isOpen ? 'text-blue-700' : 'text-gray-700'}`}>{category.title}</h3>
                            </div>
                            <div className="flex items-center space-x-3">
                                {!hasData && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">Sin datos</span>}
                                <span className={`text-gray-400 transform transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : ''}`}>
                                    ▼
                                </span>
                            </div>
                        </button>

                        <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                            <div className="p-5 bg-white border-t border-gray-100">
                                <div className="space-y-3">
                                    {category.fields.map((key) => {
                                        const value = clinicalData[key];
                                        if (value === undefined || (typeof value === 'object' && value !== null && Object.keys(value).length === 0)) return null;

                                        // Ocultar campos condicionales si sus padres no se cumplen
                                        if (key === 'valorTestElectrico' && clinicalData.testElectrico === 'noAplica') return null;
                                        if (key === 'profundidadSacoPeriodontal' && clinicalData.presenciaSacoPeriodontal !== 'si') return null;
                                        if (key === 'fracturaCoronariaOtro' && clinicalData.fracturaCoronaria !== 'otro') return null;

                                        return (
                                            <div key={key} className="grid grid-cols-1 sm:grid-cols-[40%_60%] border-b border-dashed border-gray-100 pb-2 items-start">
                                                <dt className="font-medium text-gray-500 text-sm">{formatKey(key)}</dt>
                                                <dd className="text-gray-900 font-medium">{formatValue(key, value)}</dd>
                                            </div>
                                        );
                                    })}
                                    {!hasData && (
                                        <p className="text-gray-400 italic text-sm text-center py-4">No se ingresaron datos en esta categoría.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ReasoningModule = ({ caseData, onFeedbackReceived }) => {
    const [hypothesis, setHypothesis] = useState('');
    const [justification, setJustification] = useState('');
    const [treatment, setTreatment] = useState('');
    const [otherTreatment, setOtherTreatment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingMessageIdx, setLoadingMessageIdx] = useState(0);

    const loadingMessages = [
        "Estamos procesando los datos ingresados...",
        "Leyendo la ficha clínica...",
        "Analizando tu hipótesis diagnóstica...",
        "Generando razonamiento clínico formativo...",
        "Preparando recursos y retroalimentación...",
        "Casi listo..."
    ];

    useEffect(() => {
        let interval;
        if (loading) {
            setLoadingMessageIdx(0);
            interval = setInterval(() => {
                setLoadingMessageIdx((prev) => (prev + 1) % loadingMessages.length);
            }, 4000);
        }
        return () => clearInterval(interval);
    }, [loading]);

    const handleSubmitReasoning = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (!hypothesis || !justification || !treatment || (treatment === 'Otro' && !otherTreatment)) {
            setError("Los campos de Hipótesis, Justificación y Tratamiento son obligatorios.");
            return;
        }

        const finalTreatment = treatment === 'Otro' ? otherTreatment : treatment;

        // Validación anti-duplicados (evita enviar lo mismo que la última vez)
        const lastInteraction = caseData.interactions?.[caseData.interactions.length - 1];
        if (lastInteraction &&
            lastInteraction.hypothesis === hypothesis &&
            lastInteraction.justification === justification &&
            lastInteraction.treatment === finalTreatment) {
            setError("Ya has enviado esta misma respuesta anteriormente. Si deseas intentar de nuevo, realiza algún cambio en tu análisis.");
            return;
        }

        setLoading(true);
        setError('');
        try {
            const analyzeClinicalCase = httpsCallable(functions, 'analyzeClinicalCase');
            const result = await analyzeClinicalCase({
                clinicalData: caseData.clinicalData,
                hypothesis,
                justification,
                finalTreatment
            });
            const llmFeedback = result.data.feedback;

            // 3. Guardar la interacción en Firestore
            const newInteraction = {
                hypothesis,
                justification,
                treatment: finalTreatment,
                llmFeedback,
                timestamp: Timestamp.now()
            };

            const caseDocRef = doc(db, "clinicalCases", caseData.id);
            await updateDoc(caseDocRef, {
                interactions: arrayUnion(newInteraction),
                studentEmail: auth.currentUser.email
            });

            // 4. Limpiar formulario y cambiar de pestaña
            setHypothesis('');
            setJustification('');
            setTreatment('');
            setOtherTreatment('');
            onFeedbackReceived();

        } catch (err) {
            console.error("Error en la retroalimentación:", err);
            setError("Tuvimos un problema con el servicio, vuelva a intentarlo.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Módulo de Razonamiento Clínico</h2>
            <form onSubmit={handleSubmitReasoning} className="space-y-4">
                <Input id="hypothesis" label="Hipótesis Diagnóstica (Considera el diagnóstico pulpar y periapical)" value={hypothesis} onChange={(e) => setHypothesis(e.target.value)} />
                <TextArea id="justification" label="Justificación (basada en la ficha clínica)" value={justification} onChange={(e) => setJustification(e.target.value)} />
                <Select id="treatment" label="Tratamiento Propuesto" value={treatment} onChange={(e) => setTreatment(e.target.value)}
                    options={[
                        { value: '', label: 'Seleccionar tratamiento...' },
                        { value: 'Biopulpectomía', label: 'Biopulpectomía' },
                        { value: 'Necropulpectomía', label: 'Necropulpectomía' },
                        { value: 'Retratamiento no quirúrgico', label: 'Retratamiento no quirúrgico' },
                        { value: 'Retratamiento quirúrgico', label: 'Retratamiento quirúrgico' },
                        { value: 'Otro', label: 'Otro (especificar)' }
                    ]}
                />
                {treatment === 'Otro' && (
                    <Input
                        id="otherTreatment"
                        label="Especificar otro tratamiento"
                        value={otherTreatment}
                        onChange={(e) => setOtherTreatment(e.target.value)}
                        placeholder="Escribe el tratamiento aquí..."
                        required
                    />
                )}
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <div className="flex justify-end pt-4">
                    <Button type="submit" disabled={loading} className={loading ? "animate-pulse" : ""}>
                        {loading ? (
                            <span className="flex items-center space-x-2">
                                <Spinner />
                                <span>{loadingMessages[loadingMessageIdx]}</span>
                            </span>
                        ) : "Enviar y Recibir Feedback"}
                    </Button>
                </div>
            </form>
        </Card>
    );
};

const InteractionsHistory = ({ interactions }) => {
    if (!interactions || interactions.length === 0) {
        return (
            <Card>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Historial de Interacciones</h2>
                <p className="text-gray-500 text-center py-4">Aún no has enviado tu análisis. Completa el módulo de razonamiento para recibir retroalimentación.</p>
            </Card>
        );
    }

    return (
        <div className="space-y-8">
            {interactions.slice().reverse().map((interaction, index) => (
                <Card key={index}>
                    <p className="text-sm font-semibold text-gray-500 mb-4">
                        Intento #{interactions.length - index} - {new Date(interaction.timestamp.toDate()).toLocaleString()}
                    </p>

                    <div className="mb-6 bg-gray-50 p-4 rounded-md">
                        <h4 className="font-bold text-gray-700">Tu Análisis Enviado:</h4>
                        <p><strong>Hipótesis:</strong> {interaction.hypothesis}</p>
                        <p><strong>Tratamiento:</strong> {interaction.treatment}</p>
                        <p><strong>Justificación:</strong> {interaction.justification}</p>
                    </div>

                    <FeedbackDisplay feedback={interaction.llmFeedback} />
                </Card>
            ))}
        </div>
    );
};

const FeedbackDisplay = ({ feedback }) => {
    if (!feedback || !feedback.diagnosticAccuracy) {
        return <div className="bg-yellow-100 text-yellow-800 p-3 rounded-md">Formato de retroalimentación antiguo o inválido.</div>;
    }

    const { diagnosticAccuracy, clinicalReasoning, treatmentEvaluation, learningResources, reflectiveQuestions } = feedback;

    const StatusBadge = ({ isCorrect, type }) => {
        const getLabel = () => {
            if (type === 'diagnostico') {
                return isCorrect ? 'Diagnóstico bien fundamentado' : 'El análisis diagnóstico requiere revisión';
            }
            if (type === 'tratamiento') {
                return isCorrect ? 'Tratamiento Correcto' : 'Reevaluemos el tratamiento';
            }
            return isCorrect ? 'Correcto' : 'Incorrecto';
        };

        return (
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {getLabel()}
            </div>
        );
    };

    const FeedbackSection = ({ title, icon, children }) => (
        <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="text-lg font-bold text-gray-800 flex items-center mb-3">
                <span className="mr-3 text-xl">{icon}</span> {title}
            </h4>
            <div className="text-base text-gray-700 space-y-3 pl-8">
                {children}
            </div>
        </div>
    );

    return (
        <div className="space-y-4">
            <h3 className="text-xl font-bold text-blue-700">📊 Retroalimentación Inteligente</h3>

            <FeedbackSection title="Evaluación Diagnóstica" icon="🎯">
                <StatusBadge isCorrect={diagnosticAccuracy.isCorrect} type="diagnostico" />
                <p className="mt-2">{diagnosticAccuracy.feedback}</p>
            </FeedbackSection>

            <FeedbackSection title="Razonamiento Clínico" icon="🧠">
                <p><strong>Diagnóstico sugerido:</strong> {clinicalReasoning.correctDiagnosis}</p>
                <div>
                    <strong>Hallazgos Clave:</strong>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                        {clinicalReasoning.keyFindings.map((finding, i) => <li key={i}>{finding}</li>)}
                    </ul>
                </div>
                <p><strong>Consideraciones Diferenciales:</strong> {clinicalReasoning.differentialConsiderations}</p>
            </FeedbackSection>

            <FeedbackSection title="Evaluación del Tratamiento" icon="💊">
                <StatusBadge isCorrect={treatmentEvaluation.isAppropriate} type="tratamiento" />
                <p className="mt-2"><strong>Recomendación:</strong> {treatmentEvaluation.recommendation}</p>
            </FeedbackSection>

            <FeedbackSection title="Recursos de Aprendizaje" icon="📚">
                <ul className="list-disc list-inside ml-4 space-y-1">
                    {learningResources.map((res, i) => <li key={i}>{res}</li>)}
                </ul>
            </FeedbackSection>

            <FeedbackSection title="Preguntas para Reflexión" icon="🤔">
                <ul className="list-disc list-inside ml-4 space-y-1">
                    {reflectiveQuestions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
            </FeedbackSection>
        </div>
    );
};


// --- MÓDULO DOCENTE ---
const TeacherModule = ({ navigateTo }) => {
    const [allCasesData, setAllCasesData] = useState([]);
    const [filteredCases, setFilteredCases] = useState([]);
    const [stats, setStats] = useState({ students: 0, cases: 0, responses: 0, correct: 0, incorrect: 0 });
    const [totalStudents, setTotalStudents] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        const casesQuery = query(collection(db, "clinicalCases"));
        const unsubscribeCases = onSnapshot(casesQuery, (snapshot) => {
            let allCases = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                const lastInteraction = data.interactions?.[data.interactions.length - 1];
                allCases.push({
                    id: doc.id,
                    ...data,
                    lastInteractionCorrect: lastInteraction?.llmFeedback?.diagnosticAccuracy?.isCorrect
                });
            });
            allCases.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
            setAllCasesData(allCases);
            setLoading(false);
        });

        const usersQuery = query(collection(db, "users"), where("role", "==", "student"));
        const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
            setTotalStudents(snapshot.size);
        });

        return () => {
            unsubscribeCases();
            unsubscribeUsers();
        };
    }, []);

    useEffect(() => {
        const now = new Date();
        const filtered = allCasesData.filter(c => {
            if (dateFilter === 'all') return true;
            if (!c.createdAt) return false;
            const caseDate = c.createdAt.toDate();

            if (dateFilter === 'thisMonth') {
                return caseDate.getMonth() === now.getMonth() && caseDate.getFullYear() === now.getFullYear();
            }
            if (dateFilter === 'thisYear') {
                return caseDate.getFullYear() === now.getFullYear();
            }
            if (dateFilter === 'custom') {
                if (!startDate && !endDate) return true;
                const caseTime = caseDate.getTime();
                // Si hay fecha inicio, usar esa fecha a las 00:00. Si no, 0
                const start = startDate ? new Date(startDate + 'T00:00:00').getTime() : 0;
                // Si hay fecha fin, usar esa fecha a las 23:59:59. Si no, infinito
                const end = endDate ? new Date(endDate + 'T23:59:59').getTime() : Infinity;
                return caseTime >= start && caseTime <= end;
            }
            return true;
        });

        let totalResponses = 0;
        let correctResponses = 0;

        filtered.forEach(data => {
            totalResponses += data.interactions?.length || 0;
            data.interactions?.forEach(inter => {
                if (inter.llmFeedback?.diagnosticAccuracy?.isCorrect) correctResponses++;
            });
        });

        const uniqueStudents = new Set(filtered.map(c => c.studentId).filter(Boolean)).size;

        setFilteredCases(filtered);
        setStats({
            students: uniqueStudents,
            cases: filtered.length,
            responses: totalResponses,
            correct: correctResponses,
            incorrect: totalResponses - correctResponses
        });
    }, [allCasesData, dateFilter, startDate, endDate, totalStudents]);

    return (
        <div className="container mx-auto space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                <h1 className="text-3xl font-bold text-gray-800">Módulo Docente</h1>
                <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-2">
                        <label htmlFor="dateFilter" className="text-sm font-medium text-gray-600 whitespace-nowrap">Filtrar por:</label>
                        <select
                            id="dateFilter"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 py-1.5 pl-2 pr-8"
                        >
                            <option value="all">Todas las fechas</option>
                            <option value="thisMonth">Este Mes</option>
                            <option value="thisYear">Este Año</option>
                            <option value="custom">Rango Personalizado</option>
                        </select>
                    </div>

                    {dateFilter === 'custom' && (
                        <div className="flex items-center space-x-2 pt-2 sm:pt-0 sm:border-l sm:border-gray-200 sm:pl-3">
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 py-1.5 px-2"
                            />
                            <span className="text-gray-500 text-sm">a</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 py-1.5 px-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <StatsDashboard stats={stats} />
            <EvaluationTable cases={filteredCases} loading={loading} navigateTo={navigateTo} />
        </div>
    );
};

const RoleSelectionScreen = ({ setViewAs, setCurrentPage }) => (
    <Card className="max-w-md mx-auto text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Seleccionar Vista</h2>
        <p className="text-gray-600 mb-8">¿Cómo quieres ingresar a la plataforma?</p>
        <div className="space-y-4">
            <Button onClick={() => { setViewAs('teacher'); setCurrentPage('teacher'); }} className="w-full text-lg py-3">
                Entrar como Docente
            </Button>
            <Button onClick={() => { setViewAs('student'); setCurrentPage('dashboard'); }} variant="secondary" className="w-full text-lg py-3">
                Entrar como Estudiante
            </Button>
        </div>
    </Card>
);

const PendingApprovalScreen = () => (
    <Card className="max-w-lg mx-auto text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Cuenta Pendiente de Aprobación</h2>
        <p className="text-gray-600">Gracias por registrarte. Tu cuenta está esperando la aprobación de un administrador.</p>
        <p className="text-gray-600 mt-2">Recibirás una notificación por correo electrónico una vez que tu cuenta sea activada.</p>
    </Card>
);


const StatsDashboard = ({ stats }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl shadow-sm border border-blue-200 hover:shadow-md transition-shadow duration-300 relative overflow-hidden flex flex-col items-center justify-center">
            <p className="text-4xl font-extrabold text-blue-700 mb-1 z-10">{stats.students}</p>
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide z-10 text-center">Estudiantes con fichas</p>
            <div className="absolute right-0 bottom-0 text-blue-200 opacity-40 text-6xl z-0 transform translate-x-1/4 translate-y-1/4">🎓</div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl shadow-sm border border-purple-200 hover:shadow-md transition-shadow duration-300 relative overflow-hidden flex flex-col items-center justify-center">
            <p className="text-4xl font-extrabold text-purple-700 mb-1 z-10">{stats.cases}</p>
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wide z-10 text-center">Total de Fichas</p>
            <div className="absolute right-0 bottom-0 text-purple-200 opacity-40 text-6xl z-0 transform translate-x-1/4 translate-y-1/4">📁</div>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl shadow-sm border border-indigo-200 hover:shadow-md transition-shadow duration-300 relative overflow-hidden flex flex-col items-center justify-center">
            <p className="text-4xl font-extrabold text-indigo-700 mb-1 z-10">{stats.responses}</p>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wide z-10 text-center">Iteraciones Totales</p>
            <div className="absolute right-0 bottom-0 text-indigo-200 opacity-40 text-6xl z-0 transform translate-x-1/4 translate-y-1/4">📝</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-2xl shadow-sm border border-emerald-200 hover:shadow-md transition-shadow duration-300 relative overflow-hidden flex flex-col items-center justify-center">
            <p className="text-4xl font-extrabold text-emerald-700 mb-1 z-10">{stats.correct}</p>
            <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wide z-10 text-center">Respuestas Acertadas</p>
            <div className="absolute right-0 bottom-0 text-emerald-200 opacity-40 text-6xl z-0 transform translate-x-1/4 translate-y-1/4">✅</div>
        </div>
        <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-6 rounded-2xl shadow-sm border border-rose-200 hover:shadow-md transition-shadow duration-300 relative overflow-hidden flex flex-col items-center justify-center">
            <p className="text-4xl font-extrabold text-rose-700 mb-1 z-10">{stats.incorrect}</p>
            <p className="text-sm font-semibold text-rose-600 uppercase tracking-wide z-10 text-center">Respuestas Incorrectas</p>
            <div className="absolute right-0 bottom-0 text-rose-200 opacity-40 text-6xl z-0 transform translate-x-1/4 translate-y-1/4">⚠️</div>
        </div>
    </div>
);

const EvaluationTable = ({ cases, loading, navigateTo }) => (
    <Card className="shadow-lg rounded-2xl overflow-hidden border-0">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 px-2">Historial de Evaluaciones</h2>
        <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email Estudiante</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"># Ficha</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Última Hipótesis</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Iteraciones</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Estado (Última)</th>
                        <th className="px-6 py-4"><span className="sr-only">Ver</span></th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {loading ? (
                        <tr><td colSpan="6" className="text-center py-12"><Spinner /></td></tr>
                    ) : cases.map(c => (
                        <tr key={c.id} className="hover:bg-blue-50/50 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                {c.createdAt ? new Date(c.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{c.studentEmail || 'No disponible'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 bg-gray-50/50 rounded">{c.caseId}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={c.interactions[c.interactions.length - 1]?.hypothesis}>
                                {c.interactions[c.interactions.length - 1]?.hypothesis || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 font-bold">{c.interactions?.length || 0}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {c.lastInteractionCorrect === true && <span className="px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Correcta</span>}
                                {c.lastInteractionCorrect === false && <span className="px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full bg-rose-100 text-rose-800 border border-rose-200">Incorrecta</span>}
                                {c.lastInteractionCorrect === undefined && <span className="px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full bg-gray-100 text-gray-800 border border-gray-200">Pendiente</span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <Button onClick={() => navigateTo('caseDetail', c.id)} variant="secondary" className="!py-1.5 !px-3 text-xs">Revisar Caso</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </Card>
);

// --- MÓDULO ADMINISTRADOR ---
const AdminModule = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const usersQuery = query(collection(db, "users"));
        const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
            let usersData = [];
            snapshot.forEach(doc => usersData.push({ id: doc.id, ...doc.data() }));
            setUsers(usersData);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleRoleChange = async (userId, newRole) => {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, { role: newRole });
    };

    const handleStatusChange = async (userId, newStatus) => {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, { status: newStatus });
    };

    return (
        <Card className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Administración de Usuarios</h1>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr><td colSpan="4" className="text-center py-8"><Spinner /></td></tr>
                        ) : users.map(user => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                    <select
                                        value={user.role}
                                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                        className="text-sm rounded-md border-gray-300"
                                    >
                                        <option value="student">student</option>
                                        <option value="teacher">teacher</option>
                                        <option value="admin">admin</option>
                                    </select>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.status === 'approved' ? 'bg-green-100 text-green-800' :
                                        user.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                        {user.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    {user.status === 'pending_approval' && (
                                        <>
                                            <Button onClick={() => handleStatusChange(user.id, 'approved')} variant="primary" className="text-xs">Aprobar</Button>
                                            <Button onClick={() => handleStatusChange(user.id, 'rejected')} variant="danger" className="text-xs">Rechazar</Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
